import AppGrid from "@/features/order-sheet-sets/app-grid";
import { useRecoilState } from "recoil";
import GridRowIndicator from "@/features/order-sheet-sets/grid-row-indicator";
import useIdentity from "@/features/ui/hooks/use-identity";
import { useCallback, useEffect } from "react";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useAgencyOrderSheetSetRows from "@/features/order-status/hooks/use-agency-order-sheet-set-rows";
import { agencyOrderSheetSetRowsState } from "@/features/order-status/recoils/agency-order-sheet-set-rows-state";
import { applySameStatusCheckableLock } from "@/features/order-status/helpers/agency-order-sheet-set-row-helper";
import {
  AgencyAttachmentRow,
  AgencyLineSheetRow,
} from "@/features/order-sheet-sets/line-sheet-set-row.type";
import { AgencyOrderSheetSetRow } from "@/features/order-status/order-sheet-set-row.type";
import useReadOrderSheetSetInfinite from "@/features/line-sheet-sets/hooks/use-read-order-sheet-set-infinite";
import {
  findYWRInfiniteSegment,
  postProcessYWRInfiniteData,
} from "@/features/ui/helpers/ywr-helpers";
import { readOrderSheetSetKey } from "@/features/line-sheet-sets/hooks/use-read-order-sheet-set";
import { Flex, Text } from "@chakra-ui/react";
import { readDenseOrderSheetSetProactively } from "@/features/order-sheet-sets/hooks/use-list-dense-order-sheet-set";
import useDownloadUnoFileInfinite from "@/hooks/use-download-uno-file-infinite";
import { downloadUnoFileKey } from "@/hooks/use-download-uno-file";
import { toTitle } from "@/utils/case";
import useLineSheetSetGrid from "@/features/order-sheet-sets/hooks/use-line-sheet-set-grid";
import useAppGridMutate from "@/features/ui/hooks/use-app-grid-mutate";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";
import AppGridCell from "@/features/order-sheet-sets/app-grid-cell";
import useTypedSearchParams from "@/features/invoices/hooks/use-typed-search-params";
import { PersistedOrderSheetSetListQuery } from "@/features/order-status/agency-order-sheet-set-list-page";

interface AgencyOrderSheetSetGridProps {}

export default function AgencyOrderSheetSetGrid({}: AgencyOrderSheetSetGridProps) {
  const identity = useIdentity();
  const { add: addToast, error: showError } = useAppToasts();
  const [rows, setRows] = useRecoilState(agencyOrderSheetSetRowsState);
  const { mutateRows } = useAgencyOrderSheetSetRows();
  const { discloseTooltip, hover } = useAppGridMutate(
    agencyOrderSheetSetRowsState
  );
  const { t, tCurrencyString, tLocalDateString, tLocalDateTimeString2 } =
    useI18nHelper();
  const { navigateToOrderSheetSetPage } = useRouterHelper();
  const [query] = useTypedSearchParams<PersistedOrderSheetSetListQuery>();
  // 주문시트 "전체" 탭은 여러 상태를 한꺼번에 조회하므로 전체 선택을 숨기고,
  // 단일 상태 탭에서만 전체 선택 체크박스를 노출한다.
  // TODO 현재는 전체 탭이 다중 쿼리로 가져와서 어쩔수 없이 하드코딩, 나중에 ALL 이라는 쿼리값이 생기면 변경 필요 - 2026.06.24
  const showCheckAll = (query.orderSheetStatus__in?.length ?? 0) === 1;

  const {
    disclosureUploadOrderSheetModal,
    disclosureOrderOrderSheetSetModal,
    fields,
    actions,
    extraContents,
  } = useLineSheetSetGrid({
    rows,
    discloseTooltip,
    mutateRows,
  });

  // API : Download Uploaded File

  const {
    data: downloadUnoFileData,
    fire: fireDownloadUnoFile,
    clear: clearDownloadUnoFile,
  } = useDownloadUnoFileInfinite();

  useEffect(() => {
    postProcessYWRInfiniteData(
      downloadUnoFileData,
      clearDownloadUnoFile,
      showError,
      () => {}
    );
  }, [downloadUnoFileData, clearDownloadUnoFile, showError]);

  const findDownloadUnoFileSegment = useCallback(
    (unoFileId: number) => {
      return findYWRInfiniteSegment(downloadUnoFileData, downloadUnoFileKey, {
        unoFileId,
      });
    },
    [downloadUnoFileData]
  );

  // END: API : Download Uploaded File

  // --- API : Read Order Sheet Set
  const {
    fire: fireReadOrderSheetSet,
    data: readOrderSheetSetData,
    clear: clearReadOrderSheetSet,
  } = useReadOrderSheetSetInfinite();

  useEffect(() => {
    postProcessYWRInfiniteData(
      readOrderSheetSetData,
      clearReadOrderSheetSet,
      showError
    );
  }, [readOrderSheetSetData, clearReadOrderSheetSet, showError]);

  const findReadOrderSheetSetSegment = useCallback(
    (row: AgencyOrderSheetSetRow) => {
      if (identity?.company) {
        return findYWRInfiniteSegment(
          readOrderSheetSetData,
          readOrderSheetSetKey,
          {
            by: identity.company.type,
            companyId: identity.company.id,
            orderSheetSetId: row.id,
          }
        );
      }
    },
    [identity, readOrderSheetSetData]
  );

  // --- END: API : Read Order Sheet Set

  return (
    <>
      <AppGrid
        metas={[
          {
            isCheckable: true,
            rowType: "OrderSheetSet",
            showCheckAll: showCheckAll,
            // 같은 상태끼리만 다중 선택 가능한 제약을 전체 선택에도 유지한다.
            // 체크된 행이 있으면 그 상태를, 없으면 선택 가능한 첫 행의 상태를 기준으로 잡고,
            // 비활성화된(체크 불가) 행은 무시한다.
            onCheckAll: (isChecked) => {
              setRows((prev) => {
                if (!prev) {
                  return prev;
                }
                if (!isChecked) {
                  const next = prev.map((orderSheetSet) => ({
                    ...orderSheetSet,
                    isChecked: false,
                  }));
                  applySameStatusCheckableLock(next);
                  return next;
                }
                const targetStatus =
                  prev.find((orderSheetSet) => orderSheetSet.isChecked)
                    ?.status ??
                  prev.find((orderSheetSet) => orderSheetSet.isCheckable)
                    ?.status;
                const next = prev.map((orderSheetSet) => ({
                  ...orderSheetSet,
                  isChecked:
                    !!orderSheetSet.isCheckableOriginal &&
                    orderSheetSet.status === targetStatus,
                }));
                applySameStatusCheckableLock(next);
                return next;
              });
            },
            gap: "4px",
            columns: [
              { name: toTitle(t("type")), width: "56px" },
              { name: toTitle(t("order_id")), width: "64px" },
              { name: toTitle(t("issued_by")), width: "104px" },
              { name: toTitle(t("ordered_by")), width: "104px" },
              { name: toTitle(t("title")), width: "minmax(224px, 1fr)" },
              {
                name: toTitle(t("ordered_amount.abbr")),
                width: "144px",
                textAlign: "end",
                marginRight: "24px",
              },
              {
                name: toTitle(t("confirmed_amount.abbr")),
                width: "144px",
                textAlign: "end",
                marginRight: "24px",
              },
              { name: toTitle(t("ordered_on")), width: "104px" },
              { name: toTitle(t("status")), width: "162px" },
            ],
            fields: [
              fields.lineSheetSetType,
              {
                name: "orderId",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const { id } = r as AgencyOrderSheetSetRow;
                  return `P${id}`;
                },
              },
              {
                name: "IssuedBy",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const name = (r as AgencyOrderSheetSetRow).issuedByBoutiqueName;
                  const orgName = (r as AgencyOrderSheetSetRow).issuedByBoutique.orgName;
                  return <AppGridCell value={[name, orgName]} />
                },
              },
              {
                name: "OrderedBy",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  return (r as AgencyOrderSheetSetRow).orderedByBuyerName;
                },
              },
              {
                name: "name",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const { name, id, lineSheetSetId } = r as AgencyOrderSheetSetRow;
                  return `#${lineSheetSetId} ${name}`;
                },
              },
              {
                name: "OrderedAmount",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const orderedAmount = (r as AgencyOrderSheetSetRow)
                    .orderedAmount;
                  const orderedAmountWithoutCondition = (
                    r as AgencyOrderSheetSetRow
                  ).orderedAmountWithoutCondition;
                  const orderedConditionType = (r as AgencyOrderSheetSetRow)
                    .orderedConditionType;
                  let conditionStr = "";
                  if (orderedConditionType === "DISCOUNT") {
                    conditionStr = "R- ";
                  } else if (orderedConditionType === "MARKUP") {
                    conditionStr = "C+ ";
                  } else if (orderedConditionType === "MIX") {
                    conditionStr = "R/C ";
                  }
                  return (
                    <Flex
                      flexDirection={"column"}
                      fontSize={"12px"}
                      alignItems={"flex-end"}
                      marginRight={"24px"}
                    >
                      <Text fontWeight={"700"}>
                        {tCurrencyString(orderedAmountWithoutCondition, {
                          type: "SYMBOL",
                          code: (r as AgencyOrderSheetSetRow).currency,
                        })}
                      </Text>
                      <Text>
                        ({conditionStr}
                        {tCurrencyString(orderedAmount, {
                          type: "SYMBOL",
                          code: (r as AgencyOrderSheetSetRow).currency,
                        })}
                        )
                      </Text>
                    </Flex>
                  );
                },
              },
              {
                name: "ConfirmedAmount",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const confirmedAmount = (r as AgencyOrderSheetSetRow)
                    .confirmedAmount;
                  const confirmedAmountWithoutCondition = (
                    r as AgencyOrderSheetSetRow
                  ).confirmedAmountWithoutCondition;
                  const orderedConditionType = (r as AgencyOrderSheetSetRow)
                    .confirmedConditionType;
                  let conditionStr = "";
                  if (orderedConditionType === "DISCOUNT") {
                    conditionStr = "R- ";
                  } else if (orderedConditionType === "MARKUP") {
                    conditionStr = "C+ ";
                  } else if (orderedConditionType === "MIX") {
                    conditionStr = "R/C ";
                  }
                  if (!confirmedAmount) {
                    return ""
                  }
                  return (
                    <Flex
                      flexDirection={"column"}
                      fontSize={"12px"}
                      alignItems={"flex-end"}
                      marginRight={"24px"}
                    >
                      <Text fontWeight={"700"}>
                        {tCurrencyString(confirmedAmountWithoutCondition, {
                          type: "SYMBOL",
                          code: (r as AgencyOrderSheetSetRow).currency,
                        })}
                      </Text>
                      <Text>
                        ({conditionStr}
                        {tCurrencyString(confirmedAmount, {
                          type: "SYMBOL",
                          code: (r as AgencyOrderSheetSetRow).currency,
                        })}
                        )
                      </Text>
                    </Flex>
                  );
                },
              },
              {
                name: "OrderedAt",
                chakraProps: { fontWeight: "700" },
                value: (r) => {
                  const value = (r as AgencyOrderSheetSetRow).submittedAt;
                  return tLocalDateString(value);
                },
              },
              fields.withOrderSheetStatus(false, true),
            ],
            actions: [
              actions.withDownloadUnoFile<AgencyOrderSheetSetRow>(
                (row) => row.compressedExportedFile
              ),
            ],
            indicator: (row) => {
              return (
                <GridRowIndicator
                  type={"VIEW"}
                  isHovered={row.isHovered}
                  isCollapsed={row.isCollapsed}
                />
              );
            },
            onClick: (row) => {
              const orderSheetSetRow = row as AgencyOrderSheetSetRow;
              navigateToOrderSheetSetPage(
                orderSheetSetRow.lineSheetSetId,
                orderSheetSetRow.id
              );
            },
          },
          {
            rowType: "LineSheet",
            columns: [
              { name: "type", width: "240px" },
              { name: "name", width: "1fr" },
              { name: "status", width: "144px" },
            ],
            fields: [
              fields.withRowType("line_sheet"),
              {
                name: "name",
                chakraProps: {
                  fontWeight: 700,
                },
                value: (r) => {
                  return (r as AgencyLineSheetRow).name;
                },
              },
              fields.lineSheetStatus,
            ],
            actions: [
              actions.viewLineSheet,
              actions.withDownloadUnoFile<AgencyLineSheetRow>(
                (row) => row.file
              ),
            ],
          },
          {
            rowType: "Attachment",
            columns: [
              { name: "type", width: "232px" },
              { name: "name", width: "1fr" },
              { name: "status", width: "144px" },
            ],
            fields: [
              fields.storedObjectField,
              {
                name: "name",
                value: (row) => {
                  const attachmentRow = row as AgencyAttachmentRow;
                  return attachmentRow.name;
                },
              },
            ],
            actions: [
              actions.withDownloadUnoFile<AgencyAttachmentRow>((row) => row),
            ],
          },
        ]}
        rows={rows}
        onHover={(path, row, isHovered) => {
          hover(row, isHovered);
        }}
        onClick={(path, _row) => {
          if (_row._rowType === "OrderSheetSet") {
            const row = _row as AgencyOrderSheetSetRow;

            if (!row.isRead && identity?.company) {
              readDenseOrderSheetSetProactively(row.id);

              const isLoading =
                findReadOrderSheetSetSegment(row)?.isLoading || false;
              if (!isLoading) {
                fireReadOrderSheetSet(
                  {
                    by: identity.company.type,
                    companyId: identity.company.id,
                    orderSheetSetId: row.id,
                  },
                  {}
                );
              }
            }

            // setRows((prev) => {
            //   return produce(prev, (draft) => {
            //     const target = findGridRowByPath(path, draft);
            //     if (target) {
            //       target.isCollapsed = !target.isCollapsed;
            //       target.isActive = !target.isCollapsed;
            //     }
            //   });
            // });
          }
        }}
        onCheck={(path, row, isChecked) => {
          if (!rows) {
            return;
          }
          // 클릭한 세트의 체크 상태를 반영하고, 같은 상태끼리만 선택 가능하도록 잠금을 재계산.
          // (오더시트세트만 체크 가능)
          setRows((prev) => {
            if (!prev) {
              return prev;
            }
            const next = prev.map((orderSheetSet) =>
              orderSheetSet.id === row.id
                ? { ...orderSheetSet, isChecked }
                : { ...orderSheetSet }
            );
            applySameStatusCheckableLock(next);
            return next;
          });
        }}
      ></AppGrid>
    </>
  );
}
